/**
 * Zar motoru — formül ayrıştırma, tohumlu rastgelelik, avantaj/dezavantaj.
 *
 * TASARIM KURALI: Bu modul `Math.random()` KULLANMAZ. Her atis bir `Rng`
 * parametresi alir. Boylece:
 *  - ayni tohum (seed) ayni sonucu verir  -> hile denetimi / replay
 *  - testler deterministik olur
 *  - sunucu ve istemci ayni sonucu uretebilir
 */

/** [0, 1) araliginda sayi ureten fonksiyon. */
export type Rng = () => number

/** Deterministik sozde-rastgele ureteci (mulberry32). Ayni tohum -> ayni dizi. */
export function seededRng(seed: number): Rng {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Metinden turetilmis tohum — "acik-golge-42" gibi okunabilir kayitlar icin. */
export function hashSeed(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** Kriptografik guvenli rastgelelik. Tohum verilmedikce kullanilir. */
export function secureRng(): Rng {
  return () => {
    const buf = new Uint32Array(1)
    crypto.getRandomValues(buf)
    return buf[0]! / 4294967296
  }
}

// ---------------------------------------------------------------------------
// Zar formulu
// ---------------------------------------------------------------------------

export type KeepMode = 'highest' | 'lowest' | null

export interface DiceTerm {
  /** Kac adet zar. */
  count: number
  /** Zar yuzeyi sayisi. Or. 6 -> d6. */
  sides: number
  /** Hangi zarlar tutulacak. */
  keepMode: KeepMode
  /** Kac zar tutulacak. */
  keepCount: number
}

export interface DiceFormula {
  terms: DiceTerm[]
  /** Formulun sabit eklenen kismi (+3, -2, ...) */
  flat: number
}

export class DiceParseError extends Error {
  constructor(
    message: string,
    readonly input: string,
  ) {
    super(`${message} (formul: "${input}")`)
    this.name = 'DiceParseError'
  }
}

/**
 * Zar formulunu ayristirir. Desteklenen:
 *   NdM, NdMkhN, NdMklN, bos sayida "d20", isaretli sabit ekler
 * Örnekler: "1d20+5", "2d6+3", "4d6kh3", "1d20+1d4-2", "d8"
 */
export function parseDiceFormula(input: string): DiceFormula {
  const src = input.trim().toLowerCase().replace(/\s+/g, '')
  if (!src) throw new DiceParseError('Formul bos', input)

  const terms: DiceTerm[] = []
  let flat = 0
  let consumed = 0
  let sign = 1
  let i = 0

  while (i < src.length) {
    const ch = src[i]!
    const rest = src.slice(i)

    // --- Isaret ---
    if (ch === '+' || ch === '-') {
      sign = ch === '+' ? 1 : -1
      i++
      continue
    }

    // --- Zar terimi: OZCEL OLARAK once bak ---
    // "1d20" icin rakam oneki ("1") bir SABIT degil, zar adedidir.
    // Bu yuzden once tam zar kalibini deniyoruz; bulamazsak sabit sayiya duser.
    const diceAt = /^(\d*)d(\d+)(?:k([hl])(\d+))?/i.exec(rest)
    if (diceAt) {
      const count = diceAt[1] ? Number(diceAt[1]) : 1
      const sides = Number(diceAt[2])

      if (sides < 2) throw new DiceParseError(`Gecersiz zar yuzeyi: d${sides}`, input)
      if (count < 1) throw new DiceParseError('Zar adedi sifirdan kucuk olamaz', input)
      if (count > 1000) throw new DiceParseError('Cok fazla zar (maks 1000)', input)

      let keepMode: KeepMode = null
      let keepCount = count

      if (diceAt[3]) {
        keepMode = diceAt[3].toLowerCase() === 'h' ? 'highest' : 'lowest'
        keepCount = Number(diceAt[4])
        if (keepCount < 1) {
          throw new DiceParseError('Tutulacak zar sayisi sifirdan kucuk', input)
        }
        if (keepCount > count) {
          throw new DiceParseError(
            `Tutulacak zar (${keepCount}) atilacak zarlardan (${count}) fazla`,
            input,
          )
        }
      }

      terms.push({ count, sides, keepMode, keepCount })
      const end = i + diceAt[0].length
      consumed = end
      i = end
      sign = 1
      continue
    }

    // --- Sabit sayi ---
    const intMatch = /^\d+/.exec(rest)
    if (intMatch) {
      flat += sign * Number(intMatch[0])
      i += intMatch[0].length
      consumed = i
      sign = 1
      continue
    }

    throw new DiceParseError(`Taninmayan karakter: "${ch}"`, input)
  }

  if (terms.length === 0 && flat === 0) {
    throw new DiceParseError('Formulde hicbir zar veya deger yok', input)
  }
  if (consumed !== src.length) {
    throw new DiceParseError('Formul sonuna kadar ayristirilamadi', input)
  }

  return { terms, flat }
}

// ---------------------------------------------------------------------------
// Atis
// ---------------------------------------------------------------------------

export interface DieResult {
  sides: number
  value: number
  kept: boolean
}

export interface FormulaResult {
  /** Formulun toplami (flat dahil). */
  total: number
  /** Atilan tum zarlar, tutulma durumu ile. */
  dice: DieResult[]
  /** Formulun sabit kismi. */
  flat: number
  /** Insan tarafindan okunabilir ozet. Or. "2d6+3: [4][5] +3 = 12". */
  summary: string
}

/** Tek bir zari atar. Sonuc 1..sides. */
export function rollDie(sides: number, rng: Rng): number {
  return 1 + Math.floor(rng() * sides)
}

/** Ayristirilmis formulu atar. */
export function rollFormula(formula: DiceFormula, rng: Rng): FormulaResult {
  const dice: DieResult[] = []
  let total = formula.flat

  for (const term of formula.terms) {
    const rolled: DieResult[] = []
    for (let n = 0; n < term.count; n++) {
      rolled.push({ sides: term.sides, value: rollDie(term.sides, rng), kept: true })
    }

    if (term.keepMode && term.keepCount < rolled.length) {
      // INDEKS TABANLI eleme.
      // Değere göre Set kullanmak TEKRARLI değerlerde hatalıdır:
      // ornek [6,4,4,2] + kh3 -> dogru sonuc [6,4,4], Set yontemi [6,4] verir.
      const order = rolled
        .map((die, index) => ({ die, index }))
        .sort((a, b) => {
          if (a.die.value === b.die.value) return a.index - b.index // esitlikte kararli
          return term.keepMode === 'highest' ? b.die.value - a.die.value : a.die.value - b.die.value
        })

      const keepIndexes = new Set(order.slice(0, term.keepCount).map((entry) => entry.index))
      for (const [index, die] of rolled.entries()) {
        die.kept = keepIndexes.has(index)
      }
    }

    for (const die of rolled) if (die.kept) total += die.value
    dice.push(...rolled)
  }

  return {
    total,
    dice,
    flat: formula.flat,
    summary: summarize(formula, dice, total),
  }
}

function summarize(formula: DiceFormula, dice: DieResult[], total: number): string {
  const byTerm: string[] = []
  let cursor = 0
  for (const term of formula.terms) {
    const group = dice.slice(cursor, cursor + term.count)
    cursor += term.count
    const rendered = group.map((d) => (d.kept ? `${d.value}` : `${d.value}✕`)).join(' + ')
    const keepSuffix =
      term.keepMode === 'highest'
        ? `kh${term.keepCount}`
        : term.keepMode === 'lowest'
          ? `kl${term.keepCount}`
          : ''
    byTerm.push(`${term.count}d${term.sides}${keepSuffix} [${rendered}]`)
  }
  const flatStr =
    formula.flat === 0 ? '' : ` ${formula.flat > 0 ? '+' : '-'} ${Math.abs(formula.flat)}`
  return `${byTerm.join(' | ')}${flatStr} = ${total}`
}

/** Kolaylik: formulu hem ayristirip hem atar. */
export function roll(input: string, rng: Rng = secureRng()): FormulaResult {
  return rollFormula(parseDiceFormula(input), rng)
}

// ---------------------------------------------------------------------------
// d20 testleri (avantaj / dezavantaj)
// ---------------------------------------------------------------------------

export type RollMode = 'normal' | 'advantage' | 'disadvantage'

export interface D20Result {
  /** Kullanilan mod. */
  mode: RollMode
  /** Atilan tum zar sonuclari (1 veya 2 adet). */
  rolls: number[]
  /** Uzerinden hesap yapilan zar. */
  die: number
  /** Sabit bonus (yetkinlik + bonus). */
  modifier: number
  total: number
  /** 20 = krit basari, 1 = krit basarisiz. */
  isCritical: boolean
  isFumble: boolean
}

/**
 * d20 testi atar. Advantage = iki zar atilir, yuksek olan kullanilir.
 * Deger 20 ise natuur 20; 1 ise natuur 1. (r.discardedAtisAdedi bilgisi korunur)
 */
export function rollD20(mode: RollMode, modifier: number, rng: Rng = secureRng()): D20Result {
  let rolls: number[]

  if (mode === 'normal') {
    rolls = [rollDie(20, rng)]
  } else {
    const a = rollDie(20, rng)
    const b = rollDie(20, rng)
    const pick = mode === 'advantage' ? Math.max(a, b) : Math.min(a, b)
    rolls = [pick, mode === 'advantage' ? Math.min(a, b) : Math.max(a, b)]
  }

  const die = rolls[0]!

  return {
    mode,
    rolls,
    die,
    modifier,
    total: die + modifier,
    isCritical: die === 20,
    isFumble: die === 1,
  }
}

/** Bir testin basari olup olmadigini dondurur. */
export function checkSuccess(
  result: D20Result,
  dc: number,
): {
  success: boolean
  /** 5'lik kademe: krit basari / basari / krit basarisiz / basarisiz */
  grade: 'critical-success' | 'success' | 'fumble' | 'failure'
  margin: number
} {
  const margin = result.total - dc
  if (result.isCritical) return { success: true, grade: 'critical-success', margin }
  if (result.isFumble) return { success: false, grade: 'fumble', margin }
  return margin >= 0
    ? { success: true, grade: 'success', margin }
    : { success: false, grade: 'failure', margin }
}
